/**
 * 파일명 : evl0102m_slice.ts
 * 설명   : 업무서약서 화면 Redux Slice
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0        2026-02-02    변재석    최초작성 (evl0102m.tsx에서 분리)
 ************************************************
 */

import dayjs, { Dayjs } from 'dayjs';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type {
  ITaskRow,
  IEtcRow,
  IUserInfo,
  IEvl0102MListItem,
  IEvl0102MSaveItem,
  IEvl0102MState,
  IInquiryCndsEvl0102M,
  IGrdCdOption,
} from '../../models/evl/evl0102m_model';

// ============================================================================
// 상수 정의
// ============================================================================

/**
 * 상세유형코드 상수 (DTL_TYP_CD)
 * - TEVL003 테이블에서 조회 시 필터 조건으로 사용
 * - 실제 항목ID(HR_EVL_ITM_ID)는 DB에서 동적으로 조회
 */
export const DTL_TYP_CD = {
  TASK: '24',              // 담당직무부문
  TEAM_CONTRIBUTION: '22', // 팀기여부문
  SELF_DEVELOPMENT: '23',  // 자기개발부문
} as const;

// ============================================================================
// 초기값 생성 함수
// ============================================================================

/**
 * 기본 UserInfo 생성
 */
export const createDefaultUserInfo = (): IUserInfo => ({
  userNm: '',
  grdNm: '',
  postNm: '',
  dutyNm: '',
  upperDeptNm: '',
  deptNm: '',
});

/**
 * 기본 담당직무 행 생성
 * @param id 행 ID
 * @param evlYear 평가년도
 */
export const createDefaultTaskRow = (id: number, evlYear: string): ITaskRow => ({
  id,
  name: '',
  goal: '',
  weight: '',
  period: [dayjs(`${evlYear}-01-01`), dayjs(`${evlYear}-12-31`)],
});

/**
 * 기본 팀기여 데이터 생성 (빈 배열 반환 - API에서 동적으로 채움)
 * @param evlYear 평가년도
 */
export const createDefaultTeamContribution = (evlYear: string): IEtcRow[] => [];

/**
 * 기본 자기개발 데이터 생성 (빈 배열 반환 - API에서 동적으로 채움)
 * @param evlYear 평가년도
 */
export const createDefaultSelfDevelopment = (evlYear: string): IEtcRow[] => [];

// ============================================================================
// 유틸리티 함수
// ============================================================================

/**
 * 날짜 파싱 함수 (bizPlgStDt, bizPlgEdDt를 dayjs 범위로 변환)
 * @param stDt 시작일자 (YYYYMMDD)
 * @param edDt 종료일자 (YYYYMMDD)
 */
export const parsePeriodFromDates = (stDt: string, edDt: string): [Dayjs, Dayjs] | null => {
  if (!stDt || !edDt) return null;
  const st = String(stDt).trim();
  const ed = String(edDt).trim();
  if (/^\d{8}$/.test(st) && /^\d{8}$/.test(ed)) {
    return [dayjs(st, 'YYYYMMDD'), dayjs(ed, 'YYYYMMDD')];
  }
  return null;
};

/**
 * dayjs 날짜를 YYYYMMDD 문자열로 변환
 * @param date dayjs 객체
 */
export const formatDateToYYYYMMDD = (date: Dayjs | null): string => {
  if (!date || !dayjs.isDayjs(date)) return '';
  return date.format('YYYYMMDD');
};

/**
 * 담당직무 비중 합계 계산
 * @param tasks 담당직무 배열
 */
export const calculateTotalWeight = (tasks: ITaskRow[]): number => {
  return tasks.reduce((sum, task) => {
    const weight = parseInt(task.weight || '0', 10);
    return sum + (isNaN(weight) ? 0 : weight);
  }, 0);
};

/**
 * 비중 유효성 검사 (100% 여부)
 * @param tasks 담당직무 배열
 */
export const isWeightValid = (tasks: ITaskRow[]): boolean => {
  return calculateTotalWeight(tasks) === 100;
};

/**
 * API 응답 데이터를 ITaskRow 배열로 변환 (dtlTypCd 기반)
 * @param list API 응답 리스트
 * @param evlYear 평가년도
 */
export const parseTaskListFromResponse = (
  list: IEvl0102MListItem[],
  evlYear: string
): ITaskRow[] => {
  const taskList: ITaskRow[] = [];
  let taskId = 1;

  list.forEach((item) => {
    // dtlTypCd가 '24' (담당직무부문)인 항목만 처리
    if (item.dtlTypCd === DTL_TYP_CD.TASK) {
      taskList.push({
        id: taskId++,
        hrEvlItmId: item.hrEvlItmId,
        sno: item.sno,
        name: item.taskNm || '',
        goal: item.goalCntn || '',
        weight: item.weightRt ? String(item.weightRt) : '',
        period: parsePeriodFromDates(item.bizPlgStDt || '', item.bizPlgEdDt || ''),
      });
    }
  });

  // 데이터가 없으면 기본 행 추가
  if (taskList.length === 0) {
    taskList.push(createDefaultTaskRow(1, evlYear));
  }

  return taskList;
};

/**
 * API 응답 데이터를 팀기여 IEtcRow 배열로 변환 (dtlTypCd 기반)
 * @param list API 응답 리스트
 * @param evlYear 평가년도
 */
export const parseTeamContributionFromResponse = (
  list: IEvl0102MListItem[],
  evlYear: string
): IEtcRow[] => {
  const defaultPeriod: [Dayjs, Dayjs] = [dayjs(`${evlYear}-01-01`), dayjs(`${evlYear}-12-31`)];
  const result: IEtcRow[] = [];

  // dtlTypCd가 '22' (팀기여부문)인 항목을 itmSeq 순으로 정렬하여 추가
  const teamItems = list
    .filter((item) => item.dtlTypCd === DTL_TYP_CD.TEAM_CONTRIBUTION)
    .sort((a, b) => (a.itmSeq || 0) - (b.itmSeq || 0));

  teamItems.forEach((item) => {
    // SNO는 ITM_SEQ에서 가져옴 (2자리 문자열로 변환, 예: 1 -> '01')
    const sno = item.itmSeq 
      ? String(item.itmSeq).padStart(2, '0') 
      : (item.sno || '01');
    result.push({
      key: item.hrEvlItmId || '',
      hrEvlItmId: item.hrEvlItmId,
      sno: sno,
      category: item.itmDesc || item.itmNm || '',  // ITM_DESC가 세부 구분명
      itmDesc: item.itmDesc,
      itmSeq: item.itmSeq,
      content: item.goalCntn || '',
      period: parsePeriodFromDates(item.bizPlgStDt || '', item.bizPlgEdDt || '') || defaultPeriod,
    });
  });

  return result;
};

/**
 * API 응답 데이터를 자기개발 IEtcRow 배열로 변환 (dtlTypCd 기반)
 * @param list API 응답 리스트
 * @param evlYear 평가년도
 */
export const parseSelfDevelopmentFromResponse = (
  list: IEvl0102MListItem[],
  evlYear: string
): IEtcRow[] => {
  const defaultPeriod: [Dayjs, Dayjs] = [dayjs(`${evlYear}-01-01`), dayjs(`${evlYear}-12-31`)];
  const result: IEtcRow[] = [];

  // dtlTypCd가 '23' (자기개발부문)인 항목을 itmSeq 순으로 정렬하여 추가
  const selfItems = list
    .filter((item) => item.dtlTypCd === DTL_TYP_CD.SELF_DEVELOPMENT)
    .sort((a, b) => (a.itmSeq || 0) - (b.itmSeq || 0));

  selfItems.forEach((item) => {
    // SNO는 ITM_SEQ에서 가져옴 (2자리 문자열로 변환, 예: 1 -> '01')
    const sno = item.itmSeq 
      ? String(item.itmSeq).padStart(2, '0') 
      : (item.sno || '01');
    result.push({
      key: item.hrEvlItmId || '',
      hrEvlItmId: item.hrEvlItmId,
      sno: sno,
      category: item.itmDesc || item.itmNm || '',  // ITM_DESC가 세부 구분명
      itmDesc: item.itmDesc,
      itmSeq: item.itmSeq,
      content: item.goalCntn || '',
      period: parsePeriodFromDates(item.bizPlgStDt || '', item.bizPlgEdDt || '') || defaultPeriod,
    });
  });

  return result;
};

/**
 * 담당직무를 저장용 배열로 변환 (동적 hrEvlItmId 사용)
 * @param tasks 담당직무 배열
 * @param taskHrEvlItmId 담당직무 항목ID (API에서 조회)
 */
export const convertTasksToSaveItems = (tasks: ITaskRow[], taskHrEvlItmId?: string): IEvl0102MSaveItem[] => {
  return tasks.map((task, index) => ({
    hrEvlItmId: task.hrEvlItmId || taskHrEvlItmId || '',
    sno: task.sno || String(index + 1).padStart(2, '0'),
    taskNm: task.name,
    goalCntn: task.goal,
    weightRt: parseInt(task.weight || '0', 10),
    bizPlgStDt: task.period ? formatDateToYYYYMMDD(task.period[0]) : '',
    bizPlgEdDt: task.period ? formatDateToYYYYMMDD(task.period[1]) : '',
  }));
};

/**
 * 팀기여를 저장용 배열로 변환 (동적 hrEvlItmId 사용)
 * @param teamContribution 팀기여 배열
 */
export const convertTeamContributionToSaveItems = (teamContribution: IEtcRow[]): IEvl0102MSaveItem[] => {
  return teamContribution.map((item) => ({
    hrEvlItmId: item.hrEvlItmId || item.key || '',
    sno: item.sno || '01',
    taskNm: item.category,
    goalCntn: item.content,
    bizPlgStDt: item.period ? formatDateToYYYYMMDD(item.period[0]) : '',
    bizPlgEdDt: item.period ? formatDateToYYYYMMDD(item.period[1]) : '',
  }));
};

/**
 * 자기개발을 저장용 배열로 변환 (동적 hrEvlItmId 사용)
 * @param selfDevelopment 자기개발 배열
 */
export const convertSelfDevelopmentToSaveItems = (selfDevelopment: IEtcRow[]): IEvl0102MSaveItem[] => {
  return selfDevelopment.map((item) => ({
    hrEvlItmId: item.hrEvlItmId || item.key || '',
    sno: item.sno || '01',
    taskNm: item.category,
    goalCntn: item.content,
    bizPlgStDt: item.period ? formatDateToYYYYMMDD(item.period[0]) : '',
    bizPlgEdDt: item.period ? formatDateToYYYYMMDD(item.period[1]) : '',
  }));
};

/**
 * 오늘 이후 날짜 선택 불가 (DatePicker용)
 * @param current 선택하려는 날짜
 */
export const disabledFutureDate = (current: Dayjs): boolean => {
  return current && current.isAfter(dayjs().endOf('day'));
};

// ============================================================================
// Redux Slice 정의
// ============================================================================

/**
 * 초기 상태
 */
const initialState: IEvl0102MState = {
  inquiryCnds: {
    empNo: '',
    evlYear: '',
  },
  userInfo: createDefaultUserInfo(),
  tasks: [],
  teamContribution: [],
  selfDevelopment: [],
  pledgePeriod: null,
  currentGradeStartDate: null,
  grdCd: '',
  grdCdOptions: [],
  bizPlgSubYn: 'N',
  isEditable: true,
  loading: false,
  error: null,
};

/**
 * Redux Slice
 */
export const evl0102m_Slice = createSlice({
  name: 'evl0102m_Slice',
  initialState: initialState,
  reducers: {
    // 상태 초기화
    initState: (state) => {
      Object.assign(state, { ...initialState });
    },
    // 조회조건 설정
    setInquiryCnds: (state, action: PayloadAction<Partial<IInquiryCndsEvl0102M>>) => {
      state.inquiryCnds = { ...state.inquiryCnds, ...action.payload };
    },
    // 로딩 상태 설정
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    // 에러 설정
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export default evl0102m_Slice.reducer;
export const Evl0102mAction = evl0102m_Slice.actions;
export const Evl0102mReducer = evl0102m_Slice.reducer;
